// src/components/conversation/CallTranscriptView.js
// 通話逐字稿 + 錄音播放 檢視（從 CallLogPage 抽出共用）。
// 由 CallLogPage（通話紀錄）與 CallTranscriptDrawer（訂位後台「通話紀錄」按鈕）共用，
// 確保兩處看到「完全一樣」的查看對話抽屜內容（同元件、同樣式、同音檔播放）。
//
// props:
//   log       通話紀錄物件（含 transcript / created_at / durationSeconds）
//   fontSize  泡泡字級（預設 14）
//   onBack    可選 — 有提供才顯示返回箭頭（CallLogPage 用；訂位抽屜不提供 = 不顯示）
//   onClose   關閉

import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Slider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import dayjs from 'dayjs';
import { formatTime, formatDuration } from '../../utils/callLogFormat';

const CallTranscriptView = ({ log, fontSize: fs = 14, onBack, onClose }) => {
  const transcript = log?.transcript || [];

  // Collect all customer audio URLs for sequential playback
  const audioSegments = transcript
    .map((msg, idx) => ({ idx, url: msg.audio_url }))
    .filter(s => s.url);

  const [playing, setPlaying] = useState(false);
  const [currentSegIdx, setCurrentSegIdx] = useState(0);
  const [audioProgress, setAudioProgress] = useState(0);
  const [, setAudioDuration] = useState(0);
  const [, setAudioCurrentTime] = useState(0);

  // Per-message audio state
  const [msgAudioPlaying, setMsgAudioPlaying] = useState(null);
  const msgAudioRef = useRef(null);

  // Full playback: play segments one by one
  const handlePlayPause = () => {
    if (audioSegments.length === 0) return;
    if (playing) {
      msgAudioRef.current?.pause();
      setPlaying(false);
      return;
    }
    playSegment(currentSegIdx);
  };

  const playSegment = (segIdx) => {
    if (segIdx >= audioSegments.length) {
      setPlaying(false);
      setCurrentSegIdx(0);
      setAudioProgress(0);
      return;
    }
    if (msgAudioRef.current) msgAudioRef.current.pause();
    const a = new Audio(audioSegments[segIdx].url);
    a.onended = () => {
      const next = segIdx + 1;
      setCurrentSegIdx(next);
      if (next < audioSegments.length) {
        playSegment(next);
      } else {
        setPlaying(false);
        setCurrentSegIdx(0);
        setAudioProgress(100);
      }
    };
    a.onloadedmetadata = () => setAudioDuration(a.duration);
    a.ontimeupdate = () => {
      setAudioCurrentTime(a.currentTime);
      const completedFrac = segIdx / audioSegments.length;
      const currentFrac = a.duration ? (a.currentTime / a.duration) / audioSegments.length : 0;
      setAudioProgress((completedFrac + currentFrac) * 100);
    };
    a.play();
    msgAudioRef.current = a;
    setMsgAudioPlaying(audioSegments[segIdx].idx);
    setCurrentSegIdx(segIdx);
    setPlaying(true);
  };

  // Per-message play/pause
  const handleMsgPlay = (idx, audioUrl) => {
    if (playing) {
      msgAudioRef.current?.pause();
      setPlaying(false);
    }
    if (msgAudioPlaying === idx) {
      msgAudioRef.current?.pause();
      setMsgAudioPlaying(null);
      return;
    }
    if (msgAudioRef.current) msgAudioRef.current.pause();
    const a = new Audio(audioUrl);
    a.onended = () => setMsgAudioPlaying(null);
    a.play();
    msgAudioRef.current = a;
    setMsgAudioPlaying(idx);
  };

  // Waveform bars (visual only)
  const WaveformBars = () => {
    const heights = [6, 10, 14, 8, 12, 6, 10, 14, 8, 12];
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', height: 20 }}>
        {heights.map((h, i) => (
          <Box key={i} sx={{ width: 3, height: h, borderRadius: '2px', bgcolor: '#8aafcf' }} />
        ))}
      </Box>
    );
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton size="small" onClick={onBack} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
              <ArrowBackIcon sx={{ fontSize: 18, color: '#666' }} />
            </IconButton>
          )}
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>對話紀錄</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
          <CloseIcon sx={{ fontSize: 18, color: '#666' }} />
        </IconButton>
      </Box>

      {/* Chat area */}
      <Box sx={{
        flex: 1, overflow: 'auto', bgcolor: '#ece9e4', px: 2, pt: 2, pb: 1,
      }}>
        {/* Date separator */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box component="span" sx={{
            display: 'inline-block', bgcolor: 'rgba(0,0,0,0.12)', color: '#fff',
            fontSize: 12, px: 1.5, py: '3px', borderRadius: '12px',
          }}>
            {log?.created_at ? dayjs(log.created_at).format('MM/DD') : ''}
          </Box>
        </Box>

        {transcript.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ChatIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="#999">無對話紀錄</Typography>
          </Box>
        ) : (
          transcript.map((msg, idx) => {
            const isAI = msg.role === 'assistant' || msg.role === 'ai' || msg.role === 'bot';
            const isCustomer = !isAI;
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex', mb: 2, gap: 1,
                  flexDirection: isCustomer ? 'row-reverse' : 'row',
                }}
              >
                {/* Avatar */}
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: 'white',
                  bgcolor: isAI ? '#5b9bd5' : '#4caf50',
                }}>
                  {isAI ? 'AI' : '客'}
                </Box>

                {/* Body */}
                <Box sx={{ maxWidth: '75%' }}>
                  {/* Role label */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, mb: '3px',
                    flexDirection: isCustomer ? 'row-reverse' : 'row',
                  }}>
                    <Typography sx={{ fontSize: 11, color: '#888', fontWeight: 500 }}>
                      {isAI ? 'AI助理' : '客戶'}
                    </Typography>
                  </Box>

                  {/* Bubble */}
                  <Box sx={{
                    px: 1.75, py: 1.25, fontSize: fs, lineHeight: 1.5, borderRadius: '12px',
                    wordBreak: 'break-word',
                    ...(isAI ? {
                      bgcolor: 'white', borderTopLeftRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    } : {
                      bgcolor: '#b6d7a8', color: '#1a3e1a', borderTopRightRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }),
                  }}>
                    {msg.content || msg.text || ''}

                    {/* Inline audio for customer messages */}
                    {isCustomer && msg.audio_url && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMsgPlay(idx, msg.audio_url)}
                          sx={{
                            width: 28, height: 28, bgcolor: '#3b6fa0',
                            '&:hover': { bgcolor: '#2d5a85' },
                          }}
                        >
                          {msgAudioPlaying === idx
                            ? <PauseIcon sx={{ fontSize: 14, color: 'white' }} />
                            : <PlayArrowIcon sx={{ fontSize: 14, color: 'white' }} />
                          }
                        </IconButton>
                        <WaveformBars />
                        {msg.duration && (
                          <Typography sx={{ fontSize: 12, color: '#999', ml: '2px' }}>
                            {msg.duration}s
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Timestamp */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, mt: '3px',
                    flexDirection: isCustomer ? 'row-reverse' : 'row',
                  }}>
                    <Typography sx={{ fontSize: 10, color: '#bbb' }}>
                      {(msg.ts || msg.timestamp) ? formatTime(msg.ts || msg.timestamp) : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Audio player bar — plays all customer segments sequentially */}
      {audioSegments.length > 0 && (
        <Box sx={{
          px: 2, py: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: 'white',
          display: 'flex', alignItems: 'center', gap: 1.25,
        }}>
          <IconButton
            onClick={handlePlayPause}
            sx={{
              width: 36, height: 36, bgcolor: '#145da0',
              '&:hover': { bgcolor: '#0f4a82' },
            }}
          >
            {playing
              ? <PauseIcon sx={{ fontSize: 18, color: 'white' }} />
              : <PlayArrowIcon sx={{ fontSize: 18, color: 'white' }} />
            }
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Slider
              size="small"
              value={audioProgress}
              sx={{
                color: '#145da0', height: 4, p: 0,
                '& .MuiSlider-thumb': { width: 12, height: 12 },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 11, color: '#999' }}>
                {audioSegments.length} 段錄音 {playing ? `(${currentSegIdx + 1}/${audioSegments.length})` : ''}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#999' }}>
                {formatDuration(log?.durationSeconds || 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CallTranscriptView;
